// ═════════════════════════════════════════════════════════════════════════
// COMPOSANT PARTAGÉ : ligne d'action (mai 2026 — refonte v0.4.45)
// Migration S4.1 du plan TS+Vite (2026-05-10).
// ═════════════════════════════════════════════════════════════════════════
//
// Source unique pour le rendu d'UNE ligne d'action. Utilisé par :
// - page-actions (liste globale #actions)
// - page-project (sous-ligne d'une action liée à un risque, en révision)
//
// Avant ce composant, le HTML de la ligne était dupliqué entre les 2 pages.
// Cette extraction réduit la dette technique : 1 endroit, 1 contrat.

interface ActionStatus {
  id: number;
  name?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  is_final?: boolean;
}

interface UserMapEntry {
  id: number;
  trigram?: string | null;
  name?: string;
  is_team?: boolean;
}

interface ActionLite {
  id?: number;
  text?: string;
  status_id?: number;
  priority?: number | null;
  deadline?: string | null;
  deadline_revised?: string | null;
  approver_id?: number | null;
  approver_name?: string | null;
  performers?: number[];
  consulted?: number[];
  informed?: number[];
  owner_id?: number | null;
  project_ids?: number[];
  origins?: Array<{
    project_code?: string;
    project_name?: string;
    risk_id?: number;
    risk_code?: string;
    risk_category?: string;
  }>;
  updated_at?: string;
  notes_count?: number;
  progress?: number;
  is_newly_assigned?: boolean;
  is_changed_since_seen?: boolean;
  merged_into_id?: number | null;
}

interface ProjectMapEntry {
  code?: string;
  name?: string;
}

interface ActionRowOpts {
  statuses?: ActionStatus[];
  userMap?: Record<number, UserMapEntry>;
  projectMap?: Record<number, ProjectMapEntry>;
  readOnly?: boolean;
  riskId?: number;
  idx?: number;
  extraTrAttrs?: string;
  currentUserId?: number;
  userTeamIds?: ReadonlyArray<number>;
  // Si vrai, le bouton inline d'action porte un picto/libellé « Délier » (au lieu
  // de la croix « Supprimer ») — cas des plans d'action où l'action est DÉLIÉE de
  // la phase, pas supprimée. Le data-attribut (`data-del-action-risk`) est inchangé.
  unlink?: boolean;
  // Fusion : si vrai, préfixe la ligne d'une colonne checkbox de sélection.
  // Désactivée pour les actions déjà fusionnées (merged_into_id non null).
  showCheckbox?: boolean;
  // État de sélection courant (set transmis par le parent). Permet de cocher
  // la case après re-render sans perdre la sélection utilisateur.
  selectedIds?: ReadonlySet<number>;
}

interface ActionHeadersOpts {
  // `labelHtml` est du HTML, pas du texte : les colonnes État et Fin portent
  // deux variantes de libellé (`st-long`/`st-short`, `dl-long`/`dl-short`) que
  // le CSS montre selon la largeur. Les libellés traduits sont déjà échappés
  // ici — une implémentation qui les ré-échappe affiche le balisage en clair.
  sortFn?: (key: string, labelHtml: string) => string;
  raciHeader?: string;
  // Fusion : si vrai, préfixe les headers d'une colonne checkbox vide.
  showCheckbox?: boolean;
}

interface ComponentActionRow {
  buildActionRowHtml(a: ActionLite, opts?: ActionRowOpts): string;
  buildActionHeadersHtml(opts?: ActionHeadersOpts): string;
  buildSortTh(key: string, labelHtml: string, activeKey: string, dir: 'asc' | 'desc'): string;
}

declare global {
  interface Window {
    componentActionRow: ComponentActionRow;
  }
}

(function () {
  'use strict';

  // Style CSS issu d'un statut d'action (couleur + variantes typo).
  function _statusInlineStyle(status: ActionStatus | undefined | null): string {
    if (!status) return '';
    const parts: string[] = [];
    if (status.color) parts.push('color:' + status.color);
    if (status.bold) parts.push('font-weight:700');
    if (status.italic) parts.push('font-style:italic');
    if (status.strikethrough) parts.push('text-decoration:line-through');
    return parts.join(';');
  }

  // Couleur de remplissage de la jauge d'avancement = couleur du statut, ou
  // accent par défaut si pas de statut/couleur. Toujours, indépendamment de
  // is_final (la jauge reflète l'état d'avancement, pas la finalité).
  function _gaugeFillColor(status: ActionStatus | undefined | null): string {
    return (status && status.color) || 'var(--accent)';
  }

  // Helper : trigramme d'un user (utilisé pour A et C — gain d'espace).
  // Délègue à window.trigramHtml qui pose le tooltip avec nom complet et
  // le data-team pour le gras des équipes (cf. CSS .user-trig[data-team]).
  function _userTrigram(u: UserMapEntry | undefined | null): string {
    if (!u) return '<span style="color:var(--text3)">?</span>';
    if (typeof trigramHtml === 'function') {
      return trigramHtml(u, { fallbackToName: true, placeholder: '?' });
    }
    return esc(u.trigram || u.name || '?');
  }

  // Wrap un label R/A/C dans un "tag visuel" si l'utilisateur ou une de ses
  // équipes correspond à l'ID. Permet à l'utilisateur connecté de voir d'un
  // coup d'œil à quel titre il est cité dans une action.
  function _wrapMine(html: string, isMine: boolean): string {
    if (!isMine) return html;
    return `<span class="raci-mine">${html}</span>`;
  }

  // Souligne l'entrée correspondant à l'Owner (le prochain à agir, ∈ A∪R).
  function _wrapOwner(html: string, isOwner: boolean): string {
    if (!isOwner) return html;
    return `<span style="text-decoration:underline;text-underline-offset:2px" title="${esc(t('actionRow.owner'))}">${html}</span>`;
  }

  // Vrai si `userId` correspond à l'utilisateur courant OU à l'une de ses
  // équipes (passées via opts.userTeamIds).
  function _isMineId(userId: number | null | undefined, opts: ActionRowOpts): boolean {
    if (!userId) return false;
    if (opts.currentUserId && userId === opts.currentUserId) return true;
    return Array.isArray(opts.userTeamIds) && opts.userTeamIds.includes(userId);
  }

  // Liste de trigrammes A ou C, séparés par ', '. Wrap le trigramme dans un
  // tag "raci-mine" si l'utilisateur courant ou une de ses équipes y figure.
  function _trigramList(
    ids: ReadonlyArray<number> | undefined,
    userMap: Record<number, UserMapEntry>,
    opts: ActionRowOpts,
    ownerId?: number | null,
  ): string {
    if (!ids || ids.length === 0) return '—';
    return ids
      .map((id) =>
        _wrapOwner(_wrapMine(_userTrigram(userMap[id]), _isMineId(id, opts)), id === ownerId),
      )
      .join(', ');
  }

  // Cellule fusionnée Date butoir initiale + Date de fin révisée.
  function _datesCellHtml(a: ActionLite): string {
    const init = a.deadline || null;
    const rev = a.deadline_revised || null;
    if (!init && !rev) {
      return (
        '<span style="color:var(--danger);cursor:help;font-size:14px"' +
        ` title="${esc(t('actionRow.warn.noDate'))}">⚠</span>`
      );
    }
    if (init && rev && init !== rev) {
      return (
        `<div>${esc(fmtDate(init))}</div>` + `<div>→ <strong>${esc(fmtDate(rev))}</strong></div>`
      );
    }
    const d = init || rev;
    return `<div>${esc(fmtDate(d))}</div>`;
  }

  // Cellule fusionnée RACI :
  // - (R) réalisateurs en TRIGRAMMES (N personnes ; tooltip = nom complet)
  // - (A) approbateur en NOM COMPLET (rôle de validation, mérite la lisibilité)
  // - (C) consultés en TRIGRAMMES (idem)
  // - (I) informés : volontairement absents ici, ils restent dans la modale.
  function _raciCellHtml(
    a: ActionLite,
    userMap: Record<number, UserMapEntry>,
    opts: ActionRowOpts,
  ): string {
    const performersHtml = _trigramList(a.performers, userMap, opts, a.owner_id);
    let teamsOnlyWarning = '';
    if (a.performers && a.performers.length > 0) {
      const allTeams = a.performers.every((id) => userMap[id] && userMap[id].is_team);
      if (allTeams) {
        teamsOnlyWarning =
          ' <span style="color:var(--warn);cursor:help;font-size:13px"' +
          ` title="${esc(t('actionRow.warn.teamsOnly'))}">⚠</span>`;
      }
    }
    let approverHtml: string;
    if (a.approver_id) {
      const u = userMap[a.approver_id];
      approverHtml = u ? esc(u.name || u.trigram || '?') : esc(a.approver_name || '?');
      approverHtml = _wrapMine(approverHtml, _isMineId(a.approver_id, opts));
      approverHtml = _wrapOwner(approverHtml, a.approver_id === a.owner_id);
    } else if (a.approver_name) {
      approverHtml = esc(a.approver_name);
    } else {
      approverHtml =
        '<span style="color:var(--danger);cursor:help;font-size:14px"' +
        ` title="${esc(t('actionRow.warn.noOwner'))}">⚠</span>`;
    }
    const consultedHtml = _trigramList(a.consulted, userMap, opts);
    return (
      `<div><span style="color:var(--text3);font-weight:600">(R)</span> : ${performersHtml}${teamsOnlyWarning}</div>` +
      `<div><span style="color:var(--text3);font-weight:600">(A)</span> : ${approverHtml}</div>` +
      `<div><span style="color:var(--text3);font-weight:600">(C)</span> : ${consultedHtml}</div>`
    );
  }

  // Cellule Modifié le + indicateur de notes (picto 💬 + nb).
  function _modifiedCellHtml(a: ActionLite): string {
    const datePart = a.updated_at ? a.updated_at.split('T')[0] : null;
    const dateText = datePart ? esc(fmtDate(datePart)) : '—';
    let notesBadge = '';
    if ((a.notes_count ?? 0) > 0) {
      notesBadge =
        `<div style="margin-top:2px"><span class="notes-count-badge" data-action-id="${a.id}"` +
        ` style="display:inline-flex;align-items:center;gap:2px;` +
        `background:var(--accent-bg);color:var(--accent);font-size:10px;` +
        `padding:1px 6px;border-radius:8px;cursor:help" title="">💬 ${a.notes_count}</span></div>`;
    }
    return `<div>${dateText}</div>${notesBadge}`;
  }

  // Cellule Projets : codes des projets liés. Si vide, picto avertissement
  // ORANGE (signal moins fort que le rouge des dates manquantes).
  function _projectsCellHtml(a: ActionLite, projectMap: Record<number, ProjectMapEntry>): string {
    const ids = a.project_ids || [];
    if (ids.length === 0) {
      return (
        '<span style="color:var(--warn);cursor:help;font-size:14px"' +
        ` title="${esc(t('actionRow.warn.noProject'))}">⚠</span>`
      );
    }
    const codes = ids.map((id) => esc((projectMap[id] && projectMap[id].code) || '?')).join(', ');
    const tip = ids
      .map((id) => {
        const p = projectMap[id];
        return p ? `[${p.code || '?'}] ${p.name || ''}` : '';
      })
      .filter(Boolean)
      .join('\n');
    return `<span title="${esc(tip)}">${codes}</span>`;
  }

  // Cellule Risques : liste les risques sous la forme "<codeProjet> - <codeRisque>"
  function _risksCellHtml(a: ActionLite): string {
    const origins = a.origins || [];
    if (origins.length === 0) return '<span style="color:var(--text3)">—</span>';
    return origins
      .map((o) => {
        const codeProj = o.project_code || '?';
        const codeRisk = riskCodeOrFallback({
          code: o.risk_code ?? null,
          id: o.risk_id ?? null,
        });
        const nameProj = o.project_name || '';
        const nameRisk = o.risk_category || '';
        const tip = nameProj || nameRisk ? `${nameProj} - ${nameRisk}` : '';
        return `<div title="${esc(tip)}">${esc(codeProj)} - ${esc(codeRisk)}</div>`;
      })
      .join('');
  }

  // Cellule Priorité : libellé "3 - Basse" via priorityLabel() global.
  function _priorityCellHtml(a: ActionLite): string {
    return esc(priorityLabel(a.priority));
  }

  // Cellule fusionnée État + jauge d'avancement.
  function _statusGaugeCellHtml(a: ActionLite, status: ActionStatus | undefined): string {
    const stStyle = _statusInlineStyle(status);
    const prog = typeof a.progress === 'number' ? Math.max(0, Math.min(100, a.progress)) : 0;
    const fill = _gaugeFillColor(status);
    const stName = status && status.name ? esc(status.name) : '—';
    // La jauge (barre + %) porte la classe `action-status-gauge` : masquée sur
    // mobile dans la liste #actions (demande UX juillet 2026 — on ne garde que
    // le libellé d'état). Desktop et page-projet inchangés (CSS scopée).
    return (
      `<div style="${stStyle}">${stName}</div>` +
      `<div class="action-status-gauge" style="display:flex;align-items:center;gap:4px;margin-top:3px">` +
      `<div style="width:60px;height:5px;background:var(--bg3);border-radius:4px;overflow:hidden">` +
      `<div style="height:100%;width:${prog}%;background:${fill}"></div>` +
      `</div>` +
      `<span>${prog}%</span>` +
      `</div>`
    );
  }

  // Construit le HTML d'une <tr> d'action. Source unique pour les 2 listes.
  function buildActionRowHtml(a: ActionLite, opts: ActionRowOpts = {}): string {
    const statuses = opts.statuses || [];
    const userMap = opts.userMap || {};
    const projectMap = opts.projectMap || {};
    const readOnly = !!opts.readOnly;
    const status = statuses.find((s) => s.id === a.status_id);
    const isFinal = !!(status && status.is_final);

    // Style ligne : seulement si état FINAL. En non-final, --text par défaut.
    const rowStyle = isFinal ? _statusInlineStyle(status) : '';

    // Colonne 1 : #ID — souligné comme un lien (cohérence inter-listes).
    const inlineAttrs =
      opts.riskId !== undefined && opts.idx !== undefined
        ? ` data-edit-action-inline="${opts.idx}" data-edit-action-risk="${opts.riskId}"`
        : '';
    const idLink =
      a.id != null
        ? `<a class="action-text-cell action-id-link" data-action-id="${a.id}"${inlineAttrs}` +
          ` style="text-decoration:underline;cursor:pointer;` +
          `font-weight:600;color:inherit"` +
          `>#${a.id}</a>`
        : `<span style="color:var(--text3);font-weight:600">—</span>`;

    // Colonne 10 : boutons d'action (✎ + ✕ optionnel, masqués si readOnly).
    let editBtn = '';
    let delBtn = '';
    if (!readOnly) {
      const editLbl = esc(t('actionRow.btn.edit'));
      // Mode « délier » (plans d'action) : picto chaîne brisée + libellé dédié ;
      // sinon croix « Supprimer ». Le data-attribut reste `data-del-action-risk`.
      const delLbl = esc(opts.unlink ? t('actionRow.btn.unlink') : t('actionRow.btn.delete'));
      const unlinkIcon =
        `<svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" ` +
        `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
        `style="vertical-align:-1px"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/>` +
        `<line x1="3" y1="3" x2="21" y2="21"/></svg>`;
      if (opts.riskId !== undefined && opts.idx !== undefined) {
        editBtn =
          `<button class="btn btn-secondary btn-icon" ` +
          `data-edit-action-inline="${opts.idx}" data-edit-action-risk="${opts.riskId}" ` +
          `title="${editLbl}" style="font-size:11px">✎</button>`;
        delBtn =
          ` <button class="${opts.unlink ? 'btn btn-secondary btn-icon' : 'btn btn-danger btn-icon'}" ` +
          `data-del-action-risk="${opts.riskId}" data-del-action-idx="${opts.idx}" ` +
          `title="${delLbl}" style="font-size:11px">${opts.unlink ? unlinkIcon : '✕'}</button>`;
      } else {
        editBtn =
          `<button class="btn btn-secondary btn-icon edit-action-btn" ` +
          `data-testid="action-edit-btn" ` +
          `data-action-id="${a.id}" title="${editLbl}" style="font-size:11px">✎</button>`;
      }
    }

    const trAttrs = opts.extraTrAttrs || '';
    const dataAttrs =
      `data-action-id="${a.id}"` +
      (opts.riskId !== undefined ? ` data-action-risk="${opts.riskId}"` : '') +
      (opts.idx !== undefined ? ` data-action-idx="${opts.idx}"` : '');

    let seenBadge = '';
    if (a.is_newly_assigned) {
      seenBadge =
        `<span title="${esc(t('actionRow.badge.newTitle'))}"` +
        ` style="font-size:9px;background:#facc15;color:#1a1a1a;padding:1px 6px;` +
        `border-radius:8px;font-weight:700">${esc(t('actionRow.badge.new'))}</span>`;
    } else if (a.is_changed_since_seen) {
      seenBadge =
        `<span title="${esc(t('actionRow.badge.changedTitle'))}"` +
        ` style="font-size:9px;background:#d9770622;color:#d97706;padding:1px 6px;` +
        `border-radius:8px;font-weight:600">${esc(t('actionRow.badge.changed'))}</span>`;
    }
    // Préfixe #ID visible UNIQUEMENT en mobile : sur petit écran la colonne #
    // dédiée est masquée et le numéro rejoint le titre dans la 1re colonne
    // (demande UX mobile juillet 2026). Masqué en desktop via .action-mobile-id
    // (display:none hors media query ≤768px). Le clic reste géré par la cellule
    // .action-text-cell parente (ouvre #action/:id).
    const mobileIdPrefix =
      a.id != null
        ? `<span class="action-mobile-id" style="font-weight:600;color:var(--text3);margin-right:6px">#${a.id}</span>`
        : '';
    const titleHtml = `${mobileIdPrefix}${esc(a.text || '—')}`;
    const descriptionInner = seenBadge
      ? `<div>${titleHtml}</div><div style="margin-top:3px">${seenBadge}</div>`
      : titleHtml;

    // Fusion : checkbox de sélection. Désactivée si action déjà fusionnée.
    // L'état coché est piloté par opts.selectedIds (et non par l'attribut
    // checked statique) pour survivre aux re-renders sans race condition.
    let selectCell = '';
    if (opts.showCheckbox) {
      const isMerged = a.merged_into_id != null;
      const isChecked = !!(a.id != null && opts.selectedIds && opts.selectedIds.has(a.id));
      const disabledAttr = isMerged ? ' disabled title="Action déjà fusionnée"' : '';
      const checkedAttr = isChecked ? ' checked' : '';
      selectCell =
        `<td style="width:36px;text-align:center" class="action-select-cell">` +
        `<input type="checkbox" class="action-select-checkbox" ` +
        `data-action-id="${a.id ?? ''}"${checkedAttr}${disabledAttr}></td>`;
    }

    return `<tr class="action-row" data-testid="action-row" ${dataAttrs} ${trAttrs} style="font-size:12px;${rowStyle}">
      ${selectCell}
      <td class="action-col-id" style="width:48px;text-align:center">${idLink}</td>
      <td class="action-text-cell" data-action-id="${a.id}" ${opts.riskId !== undefined ? `data-edit-action-inline="${opts.idx}" data-edit-action-risk="${opts.riskId}"` : ''}
          style="max-width:240px;cursor:pointer">${descriptionInner}</td>
      <td class="action-col-status" style="min-width:130px">${_statusGaugeCellHtml(a, status)}</td>
      <td class="action-col-priority" style="white-space:nowrap">${_priorityCellHtml(a)}</td>
      <td style="white-space:nowrap">${_datesCellHtml(a)}</td>
      <td class="action-col-raci" style="min-width:170px">${_raciCellHtml(a, userMap, opts)}</td>
      <td class="action-col-projects" style="white-space:nowrap">${_projectsCellHtml(a, projectMap)}</td>
      <td class="action-col-risks">${_risksCellHtml(a)}</td>
      <td class="action-col-updated" style="white-space:nowrap">${_modifiedCellHtml(a)}</td>
      <td class="action-col-actions" style="white-space:nowrap">${editBtn}${delBtn}</td>
    </tr>`;
  }

  // Injecte une classe CSS dans un `<th ...>` produit par sortFn. Sert de
  // point d'ancrage pour les règles responsive (masquage de colonnes en
  // mobile, par colonne, indépendamment de la présence de la checkbox).
  function _withThClass(thHtml: string, cls: string): string {
    const m = thHtml.match(/^<th([^>]*)\sclass="([^"]*)"/);
    if (m) {
      return thHtml.replace(/(^<th[^>]*\sclass=")/, `$1${cls} `);
    }
    return thHtml.replace(/^<th/, `<th class="${cls}"`);
  }

  // `sortFn` prêt à l'emploi : en-tête cliquable avec sa flèche de tri. Vit ici
  // plutôt que dans chaque page pour que le contrat de `labelHtml` (du HTML, cf.
  // ActionHeadersOpts) soit respecté par construction — le ré-échapper affichait
  // le balisage en clair sur la page Plan d'action.
  function buildSortTh(
    key: string,
    labelHtml: string,
    activeKey: string,
    dir: 'asc' | 'desc',
  ): string {
    const active = activeKey === key;
    const arrow = active ? (dir === 'asc' ? '↑' : '↓') : '↕';
    return `<th class="sortable${active ? ' sorted' : ''}" data-sort="${key}" style="cursor:pointer;user-select:none;white-space:nowrap">${labelHtml} <span style="font-size:10px">${arrow}</span></th>`;
  }

  // Headers correspondant à la nouvelle structure (10 colonnes, +1 si checkbox).
  function buildActionHeadersHtml(opts: ActionHeadersOpts = {}): string {
    const sortFn = opts.sortFn || ((_key: string, labelHtml: string) => `<th>${labelHtml}</th>`);
    const raciHeader =
      opts.raciHeader ||
      `<th class="action-raci-th" style="cursor:pointer">${esc(t('actionRow.col.raci'))}</th>`;
    const selectHeader = opts.showCheckbox
      ? '<th style="width:36px;text-align:center" class="action-select-cell"></th>'
      : '';
    return [
      selectHeader,
      _withThClass(sortFn('code', '#'), 'action-col-id'),
      sortFn('text', t('actionRow.col.description')),
      // En-tête état : libellé long « État / Avanc. » (desktop) vs court « État »
      // (mobile #actions, où la jauge d'avancement est masquée). Même bascule
      // CSS que l'échéance (.st-short masqué par défaut, révélé en mobile).
      sortFn(
        'status',
        `<span class="st-long">${esc(t('actionRow.col.statusProgress'))}</span>` +
          `<span class="st-short">${esc(t('actionRow.col.statusShort'))}</span>`,
      ),
      _withThClass(sortFn('priority', t('actionRow.col.priority')), 'action-col-priority'),
      // En-tête échéance : libellé long (desktop) vs court (mobile #actions).
      // Les 2 spans coexistent, le CSS n'en montre qu'un selon le contexte
      // (.dl-short masqué par défaut, révélé en mobile dans #actions-list-table).
      sortFn(
        'deadline',
        `<span class="dl-long">${esc(t('actionRow.col.deadline'))}</span>` +
          `<span class="dl-short">${esc(t('actionRow.col.deadlineShort'))}</span>`,
      ),
      raciHeader,
      _withThClass(sortFn('projects', t('vocabulary.project_plural')), 'action-col-projects'),
      _withThClass(sortFn('risks', t('vocabulary.risk_plural')), 'action-col-risks'),
      _withThClass(sortFn('updated', t('actionRow.col.updated')), 'action-col-updated'),
      '<th class="action-col-actions"></th>',
    ].join('');
  }

  // Expose globalement pour les pages qui consomment.
  window.componentActionRow = {
    buildActionRowHtml,
    buildActionHeadersHtml,
    buildSortTh,
  };
})();

export {};
